import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import Drawer, { DrawerCloseButton } from "../Drawer";
import { DatePickerInput } from "../DatePickerInput";
import { AddDrawerSelect } from "./AddDrawerSelect";
import { getSuppliers, ProductGroupLoadingKeys } from "../../slices/productGroupSlice";
import {
    createProductCatalogProduct,
    fetchProductCatalogChannelProducts,
    ProductCatalogLoadingKeys,
} from "../../slices/productCatalogSlice";
import { CatalogSkuPicker, ChannelSkuPicker, PackagingSkuPicker } from "./ProductCatalogProductSidebar";
import {
    CatalogInfoTip,
    CATALOG_MFG_LEAD_TIME_INFO,
    CATALOG_TOTAL_LEAD_TIME_INFO,
} from "./CatalogInfoTip";
import { CubeIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import {
    blockUnsignedIntegerKeyDown,
    sanitizeUnsignedIntegerString,
} from "../../utils/integerInput";
import { useDebounce } from "../../helpers/useDebounce";

const EMPTY_TEXT = "-";

const normalizeLeadTimePart = (value) => {
    if (value === null || value === undefined || value === "" || value === "-") return 0;
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
};

const STATUS_OPTIONS = [
    { label: "Active", value: "ACTIVE" },
    { label: "Inactive", value: "IN_ACTIVE" },
    { label: "Sunset", value: "SUNSET" },
    { label: "Planned", value: "PLANNED" },
];

/** Keys for stacking open dropdowns above sibling grid cells (see ProductCatalogProductSidebar `stackedSelectRowKey`). */
const ADD_DRAWER_STACK = {
    productGroup: "add-pg",
    status: "add-st",
    category: "add-cat",
    subcategory: "add-sub",
    brandPillar: "add-bp",
    demandTier: "add-dt",
    intent: "add-int",
    entryEngine: "add-ee",
    supplier: "add-sup",
    packaging: "add-pkg",
};

const FBM_CHANNEL_ROW = {
    field: "fbm_channel_product_id",
    skuField: "amazon_fbm_sku",
    excludeSiblingField: "fba_channel_product_id",
};
const FBA_CHANNEL_ROW = {
    field: "fba_channel_product_id",
    skuField: "amazon_fba_sku",
    excludeSiblingField: "fbm_channel_product_id",
};
const PACKAGING_ROW = {
    field: "packaging_product_id",
    skuField: "packaging_sku",
};

/** Same row shape as Flags → Replaces SKU in `ProductCatalogProductSidebar` (`DETAILS_SECTIONS`). */
const REPLACEMENT_CATALOG_ROW = {
    field: "replacement_product_id",
    skuField: "replaces_sku",
};

function SectionLabel({ children }) {
    return (
        <div className="flex items-center gap-2 mb-3">
            <div className="w-0.5 h-4 bg-gray-900 rounded-full shrink-0" />
            <span className="text-xs font-bold uppercase tracking-widest text-gray-500">{children}</span>
        </div>
    );
}

function FieldLabel({ children, required, infoTip }) {
    return (
        <div className="mb-1 flex items-center gap-1">
            <label className="text-xs text-gray-500">
                {children}
                {required && <span className="text-red-500 ml-0.5">*</span>}
            </label>
            {infoTip ? <CatalogInfoTip text={infoTip} /> : null}
        </div>
    );
}

function HighlightMatch({ text, query }) {
    const t = text ?? "";
    const q = (query ?? "").trim();
    if (!q) return <>{t}</>;
    const lower = t.toLowerCase();
    const qLower = q.toLowerCase();
    const idx = lower.indexOf(qLower);
    if (idx < 0) return <>{t}</>;
    return (
        <>
            {t.slice(0, idx)}
            <span className="text-amber-800 font-semibold">{t.slice(idx, idx + q.length)}</span>
            {t.slice(idx + q.length)}
        </>
    );
}

const handleUnsignedIntegerInputChange = (setter) => (e) => {
    const sanitized = sanitizeUnsignedIntegerString(e.target.value);
    if (sanitized === "") {
        setter("");
        return;
    }
    const n = parseInt(sanitized, 10);
    if (!Number.isFinite(n) || n < 0) return;
    setter(String(n));
};

function ToggleRow({ label, description, checked, onChange }) {
    return (
        <div className="flex items-center justify-between gap-3 py-1">
            <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900">{label}</p>
                {description && <p className="text-xs text-gray-400">{description}</p>}
            </div>
            <label className="relative inline-flex shrink-0 cursor-pointer items-center">
                <input
                    type="checkbox"
                    className="peer sr-only"
                    aria-label={label}
                    checked={checked}
                    onChange={(e) => onChange(e.target.checked)}
                />
                <div className="relative h-6 w-11 rounded-full bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 peer-checked:after:translate-x-full peer-checked:after:border-white after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all peer-checked:bg-primary" />
            </label>
        </div>
    );
}

export const ProductCatalogAddProductDrawer = ({
    isOpen,
    onClose,
    onProductCreated,
    categorySettingsItems = [],
    presetSettingsByType = {},
    containerSettingsItems = [],
}) => {
    const dispatch = useDispatch();
    const { vendorsToSelect, loadingKeys: groupLoadingKeys } = useSelector((state) => state.productGroup);
    const { channelProductsItems, loadingKeys: catalogLoadingKeys } = useSelector((state) => state.productCatalog);

    const supplierListLoading = groupLoadingKeys.includes(ProductGroupLoadingKeys.getSuppliers);
    const isCreating = catalogLoadingKeys.includes(ProductCatalogLoadingKeys.createProduct);

    const [shipHeroQuery, setShipHeroQuery] = useState("");
    const debouncedShipHeroQuery = useDebounce(shipHeroQuery, 500);
    const [shipHeroLoading, setShipHeroLoading] = useState(false);
    const [shipHeroError, setShipHeroError] = useState(null);
    const [shipHeroSearchItem, setShipHeroSearchItem] = useState(null);
    const [shipHeroExistsInCatalog, setShipHeroExistsInCatalog] = useState(false);

    const [productGroupOptions, setProductGroupOptions] = useState([]);
    const [productGroupLoading, setProductGroupLoading] = useState(false);
    const [openDrawerSelectKey, setOpenDrawerSelectKey] = useState(null);

    const [productGroupId, setProductGroupId] = useState("");
    const [variantName, setVariantName] = useState("");
    const [color, setColor] = useState("");
    const [status, setStatus] = useState("ACTIVE");
    const [supplySku, setSupplySku] = useState("");
    const [barcode, setBarcode] = useState("");
    const [categoryGroupId, setCategoryGroupId] = useState("");
    const [subcategoryVariantId, setSubcategoryVariantId] = useState("");
    const [brandPillarId, setBrandPillarId] = useState("");
    const [demandTierId, setDemandTierId] = useState("");
    const [intentId, setIntentId] = useState("");
    const [entryEngineId, setEntryEngineId] = useState("");
    const [flags, setFlags] = useState({
        virtual: false,
        isPackaging: false,
        isSupply: false,
        reusableEquipment: false,
        forecastable: false,
        needsReview: false,
        lock: false,
        replacementExit: false,
    });
    const [replacementPickerValues, setReplacementPickerValues] = useState({
        replacement_product_id: "",
        replaces_sku: "",
    });
    const [material, setMaterial] = useState("");
    const [channelValues, setChannelValues] = useState({
        fbm_channel_product_id: [],
        fba_channel_product_id: [],
        amazon_fbm_sku: "",
        amazon_fba_sku: "",
    });
    const [supplier, setSupplier] = useState("");
    const [mfgLeadTime, setMfgLeadTime] = useState("");
    const [transitLeadTime, setTransitLeadTime] = useState("");
    const [moq, setMoq] = useState("");
    const [containerGroupId, setContainerGroupId] = useState("");
    const [containerQty, setContainerQty] = useState("");
    const [cartonQty, setCartonQty] = useState("");
    const [packagingPickerValues, setPackagingPickerValues] = useState({
        packaging_product_id: [],
        packaging_sku: "",
    });
    const [unitWeight, setUnitWeight] = useState("");
    const [unitLength, setUnitLength] = useState("");
    const [unitWidth, setUnitWidth] = useState("");
    const [unitHeight, setUnitHeight] = useState("");
    const [launchDate, setLaunchDate] = useState("");
    const [sunsetDate, setSunsetDate] = useState("");
    const [inactiveDate, setInactiveDate] = useState("");

    const totalLeadTime = useMemo(
        () => String(normalizeLeadTimePart(mfgLeadTime) + normalizeLeadTimePart(transitLeadTime)),
        [mfgLeadTime, transitLeadTime]
    );

    const categoryOptions = useMemo(
        () =>
            (categorySettingsItems || []).map((group) => ({
                label: group.name,
                value: String(group.id),
            })),
        [categorySettingsItems]
    );

    const subcategoryOptions = useMemo(() => {
        const gid = Number(categoryGroupId || 0);
        if (!gid) return [];
        const group = (categorySettingsItems || []).find((g) => Number(g.id) === gid);
        return (group?.variants || []).map((v) => ({
            label: v.name,
            value: String(v.id),
        }));
    }, [categoryGroupId, categorySettingsItems]);

    const brandPillarOptions = useMemo(
        () => (presetSettingsByType?.brand_pillar || []).map((item) => ({
            label: item.name,
            value: String(item.id),
        })),
        [presetSettingsByType?.brand_pillar]
    );
    const demandTierOptions = useMemo(
        () => (presetSettingsByType?.demand_tier || []).map((item) => ({
            label: item.name,
            value: String(item.id),
        })),
        [presetSettingsByType?.demand_tier]
    );
    const intentOptions = useMemo(
        () => (presetSettingsByType?.intent || []).map((item) => ({
            label: item.name,
            value: String(item.id),
        })),
        [presetSettingsByType?.intent]
    );
    const entryEngineOptions = useMemo(
        () => (presetSettingsByType?.entry_engine || []).map((item) => ({
            label: item.name,
            value: String(item.id),
        })),
        [presetSettingsByType?.entry_engine]
    );
    const containerGroupOptions = useMemo(
        () => (containerSettingsItems || []).map((item) => ({
            label: item.name,
            value: String(item.id),
        })),
        [containerSettingsItems]
    );

    const supplierOptions = useMemo(
        () =>
            (vendorsToSelect || []).map((v) => ({
                label: v.label,
                value: String(v.value),
            })),
        [vendorsToSelect]
    );

    const resetForm = useCallback(() => {
        setShipHeroQuery("");
        setShipHeroSearchItem(null);
        setShipHeroExistsInCatalog(false);
        setShipHeroError(null);
        setProductGroupId("");
        setVariantName("");
        setColor("");
        setStatus("ACTIVE");
        setSupplySku("");
        setBarcode("");
        setCategoryGroupId("");
        setSubcategoryVariantId("");
        setBrandPillarId("");
        setDemandTierId("");
        setIntentId("");
        setEntryEngineId("");
        setFlags({
            virtual: false,
            isPackaging: false,
            isSupply: false,
            reusableEquipment: false,
            forecastable: false,
            needsReview: false,
            lock: false,
            replacementExit: false,
        });
        setReplacementPickerValues({ replacement_product_id: "", replaces_sku: "" });
        setMaterial("");
        setChannelValues({
            fbm_channel_product_id: [],
            fba_channel_product_id: [],
            amazon_fbm_sku: "",
            amazon_fba_sku: "",
        });
        setSupplier("");
        setMfgLeadTime("");
        setTransitLeadTime("");
        setMoq("");
        setContainerGroupId("");
        setContainerQty("");
        setCartonQty("");
        setPackagingPickerValues({
            packaging_product_id: [],
            packaging_sku: "",
        });
        setUnitWeight("");
        setUnitLength("");
        setUnitWidth("");
        setUnitHeight("");
        setLaunchDate("");
        setSunsetDate("");
        setInactiveDate("");
        setOpenDrawerSelectKey(null);
    }, []);

    useEffect(() => {
        if (!isOpen) {
            resetForm();
            return;
        }
        dispatch(getSuppliers({}));
    }, [dispatch, isOpen, resetForm]);

    useEffect(() => {
        if (!isOpen) return;
        let cancelled = false;
        setProductGroupLoading(true);
        axios
            .get("/api/v1/group-data", { params: { page: 1, itemsPerPage: 500 } })
            .then(({ data }) => {
                if (cancelled) return;
                const items = data?.items || [];
                setProductGroupOptions(
                    items.map((g) => ({
                        label: g.name,
                        value: String(g.id),
                    }))
                );
            })
            .catch(() => {
                if (!cancelled) {
                    setProductGroupOptions([]);
                    toast.error("Failed to load product groups");
                }
            })
            .finally(() => {
                if (!cancelled) setProductGroupLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [isOpen]);

    useEffect(() => {
        const q = debouncedShipHeroQuery.trim();
        if (!isOpen) return;
        if (!q) {
            setShipHeroSearchItem(null);
            setShipHeroExistsInCatalog(false);
            setShipHeroError(null);
            setShipHeroLoading(false);
            return;
        }
        let cancelled = false;
        setShipHeroLoading(true);
        setShipHeroError(null);
        axios
            .get("/api/v1/product-catalog/shiphero-search", { params: { sku: q } })
            .then(({ data }) => {
                if (cancelled) return;
                setShipHeroSearchItem(data?.item || null);
                setShipHeroExistsInCatalog(Boolean(data?.existsInCatalog));
            })
            .catch((err) => {
                if (cancelled) return;
                setShipHeroSearchItem(null);
                setShipHeroExistsInCatalog(false);
                const msg =
                    err.response?.data?.error ||
                    err.response?.data?.errors ||
                    err.message ||
                    "ShipHero search failed";
                setShipHeroError(typeof msg === "string" ? msg : "ShipHero search failed");
            })
            .finally(() => {
                if (!cancelled) setShipHeroLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [debouncedShipHeroQuery, isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        const bc = String(barcode || "").trim();
        if (!bc || bc === "-") return;
        dispatch(
            fetchProductCatalogChannelProducts({
                barcode: bc,
                productId: shipHeroSearchItem?.id,
            })
        );
    }, [barcode, dispatch, isOpen, shipHeroSearchItem?.id]);

    const canonicalName =
        productGroupOptions.find((o) => o.value === productGroupId)?.label && variantName
            ? `${productGroupOptions.find((o) => o.value === productGroupId)?.label} - ${variantName}`
            : productGroupOptions.find((o) => o.value === productGroupId)?.label || variantName || "";

    const cubicInches = (() => {
        const l = parseFloat(unitLength);
        const w = parseFloat(unitWidth);
        const h = parseFloat(unitHeight);
        if (Number.isFinite(l) && Number.isFinite(w) && Number.isFinite(h)) {
            return (l * w * h).toFixed(2);
        }
        return null;
    })();

    const setFlag = (key, value) => {
        setFlags((prev) => ({
            ...prev,
            [key]: value,
            ...(key === "isSupply" && !value ? { reusableEquipment: false } : {}),
        }));
    };

    const handleChannelFieldChange = (field, value) => {
        setChannelValues((prev) => ({ ...prev, [field]: value }));
    };

    useEffect(() => {
        if (!shipHeroSearchItem) {
            setSupplySku("");
            setBarcode("");
            setVariantName("");
            return;
        }
        setSupplySku(shipHeroSearchItem.sku || "");
        setBarcode(shipHeroSearchItem.barcode || "");
        setVariantName(shipHeroSearchItem.name || "");
    }, [shipHeroSearchItem]);

    const channelItemStub = useMemo(
        () => ({
            barcode: barcode && barcode !== "-" ? barcode : EMPTY_TEXT,
            id: shipHeroSearchItem?.id,
        }),
        [barcode, shipHeroSearchItem?.id]
    );

    const isReusableSupply = flags.isSupply && flags.reusableEquipment;
    const canSubmit =
        Boolean(shipHeroSearchItem?.id) &&
        (isReusableSupply || Boolean(productGroupId)) &&
        (isReusableSupply || Boolean(categoryGroupId)) &&
        !shipHeroExistsInCatalog &&
        !isCreating;

    const handleSubmit = async () => {
        if (!canSubmit || !shipHeroSearchItem) return;
        try {
            await dispatch(
                createProductCatalogProduct({
                    shiphero_sku: shipHeroSearchItem.sku,
                    product_group_id: productGroupId || null,
                    category_group_id: categoryGroupId || null,
                    subcategory_variant_id: subcategoryVariantId || null,
                    brand_pillar_id: brandPillarId || null,
                    demand_tier_id: demandTierId || null,
                    intent_id: intentId || null,
                    entry_engine_id: entryEngineId || null,
                    status,
                    weight: unitWeight || null,
                    length: unitLength || null,
                    width: unitWidth || null,
                    height: unitHeight || null,
                    is_virtual: flags.virtual,
                    is_packaging: flags.isPackaging,
                    is_supply: flags.isSupply,
                    reusable_equipment: flags.isSupply ? flags.reusableEquipment : false,
                    fbm_channel_product_id: channelValues.fbm_channel_product_id,
                    fba_channel_product_id: channelValues.fba_channel_product_id,
                    packaging_product_id: packagingPickerValues.packaging_product_id,
                    forecastable: flags.forecastable,
                    needs_review: flags.needsReview,
                    lock_flag: flags.lock,
                    replacement_exit: flags.replacementExit,
                    replacement_product_id: flags.replacementExit
                        ? replacementPickerValues.replacement_product_id || null
                        : null,
                    lead_time_days: mfgLeadTime || null,
                    transit_lead_time_days: transitLeadTime || null,
                    moq: moq || null,
                    container_group_id: containerGroupId || null,
                    container_qty: containerQty || null,
                    carton_qty: cartonQty || null,
                    launch_date: launchDate || null,
                    sunset_date: sunsetDate || null,
                    inactive_date: inactiveDate || null,
                    color: color || null,
                    material: material || null,
                    vendor_id: supplier || null,
                })
            ).unwrap();
            toast.success("Product created");
            onClose();
            onProductCreated?.();
            resetForm();
        } catch (err) {
            const msg = typeof err === "string" ? err : err?.message || "Failed to create product";
            toast.error(msg);
        }
    };

    const highlightQuery = debouncedShipHeroQuery.trim();

    return (
        <Drawer
            isOpen={isOpen}
            onClose={onClose}
            title=""
            width="500px"
            closeButton={<DrawerCloseButton onClose={onClose} />}
            showActionButtons
            submitButtonText="Save Product"
            submitDisabled={!canSubmit}
            onSubmit={handleSubmit}
            headerContent={
                <div className="flex min-w-0 flex-1 items-center gap-2 pr-2">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-gray-100">
                        <CubeIcon className="h-4 w-4 text-gray-600" />
                    </div>
                    <h2 className="truncate text-base font-bold text-gray-900">Add New Product</h2>
                </div>
            }
        >
            <div className="px-6 py-5">
                <div className="space-y-6">
                    <div>
                        <SectionLabel>ShipHero Import</SectionLabel>
                        <div className="rounded-lg border border-blue-100 bg-blue-50/60 p-3">
                            <FieldLabel>Search by SKU, name, or barcode</FieldLabel>
                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder="(Only exact values)"
                                    value={shipHeroQuery}
                                    onChange={(e) => setShipHeroQuery(e.target.value)}
                                    className="w-full rounded-md border border-blue-200 bg-white py-2 pl-3 pr-9 text-sm text-gray-900 placeholder:text-gray-400 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
                                />
                                <MagnifyingGlassIcon className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                            </div>
                            {shipHeroLoading && (
                                <p className="mt-2 text-xs text-gray-500">Searching ShipHero…</p>
                            )}
                            {shipHeroError && (
                                <p className="mt-2 text-xs text-red-600">{shipHeroError}</p>
                            )}
                            {!shipHeroLoading && !shipHeroError && highlightQuery && shipHeroSearchItem && (
                                <ul className="mt-2 max-h-48 overflow-y-auto rounded-md border border-gray-200 bg-white">
                                    <li className="flex items-start gap-2 px-3 py-2">
                                        <span className="inline-flex shrink-0 rounded border border-gray-200 bg-gray-100 px-1.5 py-0.5 font-mono text-xs text-gray-800">
                                            <HighlightMatch text={shipHeroSearchItem.sku} query={highlightQuery} />
                                        </span>
                                        <span className="min-w-0 text-sm text-gray-800">
                                            {shipHeroSearchItem.name || "—"}
                                        </span>
                                    </li>
                                </ul>
                            )}
                            {!shipHeroLoading &&
                                highlightQuery &&
                                !shipHeroSearchItem &&
                                !shipHeroError &&
                                debouncedShipHeroQuery.trim() && (
                                    <p className="mt-2 text-xs text-gray-500">No ShipHero product for this exact SKU.</p>
                                )}
                        </div>
                        {!shipHeroLoading &&
                            shipHeroSearchItem &&
                            shipHeroExistsInCatalog && (
                                <p className="mt-2 text-sm text-amber-900">
                                    This product already exists in your catalog. You cannot add it again.
                                </p>
                            )}
                    </div>

                    <div className="border-t border-gray-200" />

                    <div>
                        <SectionLabel>Identity</SectionLabel>
                        <div className="space-y-1.5">
                            <div>
                                <FieldLabel>Canonical Name</FieldLabel>
                                <div title={canonicalName} className="flex h-9 cursor-not-allowed items-center rounded-md truncate border border-gray-200 bg-gray-50 px-3 text-sm text-gray-500">
                                    {canonicalName || (
                                        <span className="italic text-gray-400">Auto: Product group – Variant</span>
                                    )}
                                </div>
                            </div>
                            <div>
                                <FieldLabel required={!isReusableSupply}>Product Name</FieldLabel>
                                <AddDrawerSelect
                                    options={productGroupOptions}
                                    value={productGroupId}
                                    onChange={setProductGroupId}
                                    noneLabel={productGroupLoading ? "Loading groups…" : "Select product group…"}
                                    isLoading={productGroupLoading}
                                    disabled={productGroupLoading}
                                    stackKey={ADD_DRAWER_STACK.productGroup}
                                    openStackKey={openDrawerSelectKey}
                                    onStackKeyChange={setOpenDrawerSelectKey}
                                />
                                <p className="mt-1 text-xs text-gray-400">Loaded from product groups (group-data).</p>
                            </div>
                            <div>
                                <FieldLabel>Variant</FieldLabel>
                                <input
                                    type="text"
                                    readOnly
                                    disabled
                                    value={variantName}
                                    title={variantName}
                                    placeholder="Select a ShipHero product above"
                                    className="w-full cursor-not-allowed truncate rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600"
                                />
                            </div>
                            <div>
                                <FieldLabel>Color</FieldLabel>
                                <input
                                    type="text"
                                    placeholder="e.g., Truffle"
                                    value={color}
                                    onChange={(e) => setColor(e.target.value)}
                                    className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary"
                                />
                            </div>
                            <div>
                                <FieldLabel>Status</FieldLabel>
                                <AddDrawerSelect
                                    options={STATUS_OPTIONS}
                                    value={status}
                                    onChange={setStatus}
                                    noneLabel="Select status"
                                    selectClassname="w-36"
                                    stackKey={ADD_DRAWER_STACK.status}
                                    openStackKey={openDrawerSelectKey}
                                    onStackKeyChange={setOpenDrawerSelectKey}
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <FieldLabel>Supply SKU</FieldLabel>
                                    <input
                                        type="text"
                                        readOnly
                                        disabled
                                        placeholder="Auto from ShipHero"
                                        value={supplySku}
                                        className="w-full cursor-not-allowed rounded-md border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-sm text-gray-600"
                                    />
                                </div>
                                <div>
                                    <FieldLabel>Barcode</FieldLabel>
                                    <input
                                        type="text"
                                        readOnly
                                        disabled
                                        placeholder="Auto from ShipHero"
                                        value={barcode}
                                        className="w-full cursor-not-allowed rounded-md border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-sm text-gray-600"
                                    />
                                </div>
                            </div>
                            <div>
                                <ToggleRow
                                    label="Packaging"
                                    description="This SKU is a packaging component"
                                    checked={flags.isPackaging}
                                    onChange={(v) => setFlag("isPackaging", v)}
                                />
                            </div>
                            <div>
                                <ToggleRow
                                    label="Supplies"
                                    description="This SKU is a supply or consumable"
                                    checked={flags.isSupply}
                                    onChange={(v) => setFlag("isSupply", v)}
                                />
                            </div>
                            {flags.isSupply && (
                                <div>
                                    <ToggleRow
                                        label="Reusable warehouse equipment"
                                        description="Reusable containers/equipment (e.g. spare bins). Excluded from replenishment and Deep Reserve suggestions. Not sold."
                                        checked={flags.reusableEquipment}
                                        onChange={(v) => setFlag("reusableEquipment", v)}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="border-t border-gray-200" />

                    <div>
                        <SectionLabel>Classification</SectionLabel>
                        <div className="space-y-1.5">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <FieldLabel required={!isReusableSupply}>Category</FieldLabel>
                                    <AddDrawerSelect
                                        options={categoryOptions}
                                        value={categoryGroupId}
                                        onChange={(v) => {
                                            setCategoryGroupId(v);
                                            setSubcategoryVariantId("");
                                        }}
                                        noneLabel="Select…"
                                        allLabel="All categories"
                                        stackKey={ADD_DRAWER_STACK.category}
                                        openStackKey={openDrawerSelectKey}
                                        onStackKeyChange={setOpenDrawerSelectKey}
                                    />
                                    {isReusableSupply && (
                                        <p className="mt-1 text-xs text-gray-400">
                                            Optional for reusable warehouse equipment. Leave blank unless a category truly applies; assigning one changes month-end inventory and profit-report scope.
                                        </p>
                                    )}
                                </div>
                                <div>
                                    <FieldLabel>Subcategory</FieldLabel>
                                    <AddDrawerSelect
                                        options={subcategoryOptions}
                                        value={subcategoryVariantId}
                                        onChange={setSubcategoryVariantId}
                                        noneLabel="Select…"
                                        allLabel="All subcategories"
                                        disabled={!categoryGroupId}
                                        stackKey={ADD_DRAWER_STACK.subcategory}
                                        openStackKey={openDrawerSelectKey}
                                        onStackKeyChange={setOpenDrawerSelectKey}
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <FieldLabel>Brand Pillar</FieldLabel>
                                    <AddDrawerSelect
                                        options={brandPillarOptions}
                                        value={brandPillarId}
                                        onChange={setBrandPillarId}
                                        noneLabel="Select…"
                                        allLabel="All pillars"
                                        stackKey={ADD_DRAWER_STACK.brandPillar}
                                        openStackKey={openDrawerSelectKey}
                                        onStackKeyChange={setOpenDrawerSelectKey}
                                    />
                                </div>
                                <div>
                                    <FieldLabel>Demand Tier</FieldLabel>
                                    <AddDrawerSelect
                                        options={demandTierOptions}
                                        value={demandTierId}
                                        onChange={setDemandTierId}
                                        noneLabel="Select…"
                                        allLabel="All tiers"
                                        stackKey={ADD_DRAWER_STACK.demandTier}
                                        openStackKey={openDrawerSelectKey}
                                        onStackKeyChange={setOpenDrawerSelectKey}
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <FieldLabel>Intent</FieldLabel>
                                    <AddDrawerSelect
                                        options={intentOptions}
                                        value={intentId}
                                        onChange={setIntentId}
                                        noneLabel="Select…"
                                        allLabel="All intents"
                                    />
                                </div>
                                <div>
                                    <FieldLabel>Entry Engine</FieldLabel>
                                    <AddDrawerSelect
                                        options={entryEngineOptions}
                                        value={entryEngineId}
                                        onChange={setEntryEngineId}
                                        noneLabel="Select…"
                                        allLabel="All entry engines"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="border-t border-gray-200" />

                    <div>
                        <SectionLabel>Flags</SectionLabel>
                        <div className="space-y-1.5">
                            <ToggleRow
                                label="Virtual Product"
                                description="Non-physical or bundled product"
                                checked={flags.virtual}
                                onChange={(v) => setFlag("virtual", v)}
                            />
                            <ToggleRow
                                label="Forecastable"
                                description="Include in demand forecasting"
                                checked={flags.forecastable}
                                onChange={(v) => setFlag("forecastable", v)}
                            />
                            <ToggleRow
                                label="Needs Review"
                                description="Flag for team review"
                                checked={flags.needsReview}
                                onChange={(v) => setFlag("needsReview", v)}
                            />
                            <ToggleRow
                                label="Lock Flag"
                                description="Prevent further edits"
                                checked={flags.lock}
                                onChange={(v) => setFlag("lock", v)}
                            />
                            <ToggleRow
                                label="Replacement Exit"
                                description="This product replaces another"
                                checked={flags.replacementExit}
                                onChange={(v) => {
                                    setFlag("replacementExit", v);
                                    if (!v) {
                                        setReplacementPickerValues({
                                            replacement_product_id: "",
                                            replaces_sku: "",
                                        });
                                    }
                                }}
                            />
                            {flags.replacementExit && shipHeroSearchItem?.id && (
                                <div className="ml-1 border-l-2 border-gray-200 pl-3">
                                    <div className="group flex items-center justify-between gap-3 rounded-lg px-0 py-1.5 transition-colors">
                                        <div className="min-w-0 flex flex-col shrink-0">
                                            <p className="text-xs font-normal text-gray-500">Replaces SKU</p>
                                        </div>
                                        <div className="min-w-0 max-w-[210px] flex-1 pl-2 text-right">
                                            <CatalogSkuPicker
                                                row={REPLACEMENT_CATALOG_ROW}
                                                values={replacementPickerValues}
                                                item={{ id: shipHeroSearchItem.id }}
                                                onChange={(field, value) => {
                                                    setReplacementPickerValues((prev) => ({
                                                        ...prev,
                                                        [field]: value,
                                                    }));
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="border-t border-gray-200" />

                    <div>
                        <SectionLabel>Attributes</SectionLabel>
                        <div>
                            <FieldLabel>Material</FieldLabel>
                            <textarea
                                placeholder="e.g., Full-Grain Leather"
                                value={material}
                                onChange={(e) => setMaterial(e.target.value)}
                                rows={4}
                                className="w-full min-h-[88px] resize-y rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary"
                            />
                        </div>
                    </div>

                    <div className="border-t border-gray-200" />

                    <div>
                        <SectionLabel>Channel</SectionLabel>
                        <div className="space-y-1.5">
                            <div className="flex items-start justify-between gap-3 border-b border-gray-100 py-2">
                                <span className="text-sm text-gray-500 pt-1">FBM SKU</span>
                                <ChannelSkuPicker
                                    row={FBM_CHANNEL_ROW}
                                    values={channelValues}
                                    item={channelItemStub}
                                    onChange={handleChannelFieldChange}
                                    dynamicOptions={{ channelProductOptions: channelProductsItems }}
                                />
                            </div>
                            <div className="flex items-start justify-between gap-3 py-2">
                                <span className="text-sm text-gray-500 pt-1">FBA SKU</span>
                                <ChannelSkuPicker
                                    row={FBA_CHANNEL_ROW}
                                    values={channelValues}
                                    item={channelItemStub}
                                    onChange={handleChannelFieldChange}
                                    dynamicOptions={{ channelProductOptions: channelProductsItems }}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="border-t border-gray-200" />

                    <div>
                        <SectionLabel>Operations</SectionLabel>
                        <div className="space-y-1.5">
                            <div>
                                <FieldLabel>Supplier</FieldLabel>
                                <AddDrawerSelect
                                    options={supplierOptions}
                                    value={supplier}
                                    onChange={setSupplier}
                                    noneLabel="Select supplier…"
                                    allLabel="All suppliers"
                                    isLoading={supplierListLoading}
                                    disabled={supplierListLoading}
                                    stackKey={ADD_DRAWER_STACK.supplier}
                                    openStackKey={openDrawerSelectKey}
                                    onStackKeyChange={setOpenDrawerSelectKey}
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <FieldLabel infoTip={CATALOG_MFG_LEAD_TIME_INFO}>Mfg Lead Time (days)</FieldLabel>
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        autoComplete="off"
                                        placeholder="e.g., 60"
                                        value={mfgLeadTime}
                                        onKeyDown={blockUnsignedIntegerKeyDown}
                                        onChange={handleUnsignedIntegerInputChange(setMfgLeadTime)}
                                        className="w-full rounded-md border border-gray-200 px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                                    />
                                </div>
                                <div>
                                    <FieldLabel>Transit Time (days)</FieldLabel>
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        autoComplete="off"
                                        placeholder="e.g., 14"
                                        value={transitLeadTime}
                                        onKeyDown={blockUnsignedIntegerKeyDown}
                                        onChange={handleUnsignedIntegerInputChange(setTransitLeadTime)}
                                        className="w-full rounded-md border border-gray-200 px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                                    />
                                </div>
                                <div>
                                    <FieldLabel infoTip={CATALOG_TOTAL_LEAD_TIME_INFO}>Total Lead Time (days)</FieldLabel>
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        autoComplete="off"
                                        placeholder="0"
                                        value={totalLeadTime}
                                        disabled
                                        readOnly
                                        className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-sm text-gray-600 cursor-not-allowed"
                                    />
                                </div>
                                <div>
                                    <FieldLabel>MOQ</FieldLabel>
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        autoComplete="off"
                                        placeholder="e.g., 100"
                                        value={moq}
                                        onKeyDown={blockUnsignedIntegerKeyDown}
                                        onChange={handleUnsignedIntegerInputChange(setMoq)}
                                        className="w-full rounded-md border border-gray-200 px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <FieldLabel>Container Group</FieldLabel>
                                    <AddDrawerSelect
                                        options={containerGroupOptions}
                                        value={containerGroupId}
                                        onChange={setContainerGroupId}
                                        noneLabel="Select…"
                                        allLabel="All container groups"
                                    />
                                </div>
                                <div>
                                    <FieldLabel>Container QTY</FieldLabel>
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        autoComplete="off"
                                        placeholder="e.g., 500"
                                        value={containerQty}
                                        onKeyDown={blockUnsignedIntegerKeyDown}
                                        onChange={handleUnsignedIntegerInputChange(setContainerQty)}
                                        className="w-full rounded-md border border-gray-200 px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <FieldLabel>Carton QTY</FieldLabel>
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        autoComplete="off"
                                        placeholder="e.g., 100"
                                        value={cartonQty}
                                        onKeyDown={blockUnsignedIntegerKeyDown}
                                        onChange={handleUnsignedIntegerInputChange(setCartonQty)}
                                        className="w-full rounded-md border border-gray-200 px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                                    />
                                </div>
                            </div>
                            <div className="flex items-start justify-between gap-3 py-2">
                                <span className="text-sm text-gray-500 pt-1">Packaging SKU</span>
                                <div
                                    className={
                                        openDrawerSelectKey === ADD_DRAWER_STACK.packaging
                                            ? "relative z-[200]"
                                            : "relative"
                                    }
                                >
                                    <PackagingSkuPicker
                                        row={PACKAGING_ROW}
                                        values={packagingPickerValues}
                                        item={{ id: shipHeroSearchItem?.id }}
                                        onChange={(field, value) => {
                                            setPackagingPickerValues((prev) => ({
                                                ...prev,
                                                [field]: value,
                                            }));
                                        }}
                                        onOpenChange={(open) => {
                                            setOpenDrawerSelectKey(open ? ADD_DRAWER_STACK.packaging : null);
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="border-t border-gray-200" />

                    <div>
                        <SectionLabel>Physical</SectionLabel>
                        <div className="space-y-1.5">
                            <div className="grid grid-cols-4 gap-2">
                                {[
                                    ["Weight (lbs)", unitWeight, setUnitWeight],
                                    ["Length (in)", unitLength, setUnitLength],
                                    ["Width (in)", unitWidth, setUnitWidth],
                                    ["Height (in)", unitHeight, setUnitHeight],
                                ].map(([lab, val, setter]) => (
                                    <div key={lab}>
                                        <FieldLabel>{lab}</FieldLabel>
                                        <input
                                            type="number"
                                            step="0.01"
                                            placeholder="0.0"
                                            value={val}
                                            onChange={(e) => setter(e.target.value)}
                                            className="no-arrows-by-input w-full rounded-md border border-gray-200 px-2 py-1.5 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                                        />
                                    </div>
                                ))}
                            </div>
                            <div className="flex items-center justify-between border-t border-gray-100 pt-2">
                                <span className="text-sm text-gray-500">Cubic Inches</span>
                                <span className="font-mono text-sm text-gray-900">
                                    {cubicInches ?? <span className="text-gray-400">—</span>}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="border-t border-gray-200" />

                    <div>
                        <SectionLabel>Lifecycle</SectionLabel>
                        <div className="space-y-2">
                            {[
                                { label: "Launch Date", value: launchDate, set: setLaunchDate },
                                { label: "Sunset Date", value: sunsetDate, set: setSunsetDate },
                                { label: "Inactive Date", value: inactiveDate, set: setInactiveDate },
                            ].map(({ label, value, set }) => (
                                <div
                                    key={label}
                                    className="flex items-center justify-between border-b border-gray-100 py-2 last:border-0"
                                >
                                    <span className="text-sm text-gray-500">{label}</span>
                                    <div className="w-40" onMouseDown={(e) => e.stopPropagation()}>
                                        <DatePickerInput
                                            value={value}
                                            min={'2018-01-01'}
                                            max={`${new Date().getFullYear() + 5}-12-31`}
                                            onChange={set}
                                            placeholder="Pick date"
                                            pickerClassName="!right-0"
                                            buttonClassName="!py-1.5 !text-sm"
                                            className="w-full"
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </Drawer>
    );
};
